// Scoring rules and the edit model shared by the daily-entry screen and the grid, so both compute
// points, pick a rating tier and decide what to save in exactly the same way.
// See SPECIFIKACE.md ch. 5 (scoring) and ch. 8 points 2-3 (the two editors).

/** Points earned for [rawValue] units of this exercise: raw units over the exercise's norm. */
fun ExerciseType.pointsFor(rawValue: Double): Double = rawValue / pointsDivisor

/** The highest tier whose threshold [points] still reaches, or null when none applies. */
fun List<RatingTier>.tierFor(points: Double): RatingTier? =
    filter { points >= it.minPoints }.maxByOrNull { it.minPoints }

/** One participant's place on a leaderboard. */
data class Standing(
    val participant: Participant,
    val points: Double,
    val rank: Int,
    /** Days in the aggregated range that counted as completed (SPECIFIKACE.md ch. 5 point 6). */
    val completedDays: Int = 0,
    /** Streak state; only the all-time board has one to show. */
    val streak: Streak? = null,
)

// ---------------------------------------------------------------------------------------------
// Streaks (PLAN-STREAK.md)
//
// The server owns the fold: cv_streak_day holds every settled day and cv_streaks hands back the
// state through yesterday alongside the state with today applied. The client's only job is to
// redo THAT LAST STEP as the user types, so the badges move with the numbers in the form. Keeping
// it to one step is deliberate -- a second full implementation of the rules in Kotlin would drift
// from the SQL one the first time anything changed.
// ---------------------------------------------------------------------------------------------

/** The streak badges as they stand once today's points are counted. */
data class StreakPreview(
    val levels: List<StreakLevel>,
    /** How many levels today's points have reached; 0 before anything is logged. */
    val todayLevel: Int,
    /** The streak is riding on a spent freeze and today has not saved it yet. */
    val pending: Boolean,
    /** Today's points just repaid a frozen day. */
    val repaid: Boolean,
) {
    /** Levels with a run actually going, weakest first -- the only ones worth a badge. */
    val live: List<StreakLevel> get() = levels.filter { it.streak > 0 }

    /**
     * Whether today has already carried the level at [index] (0-based, weakest first).
     *
     * This is the whole difference between "SUPERBOREC 7, banked today" and "SUPERBOREC 6, still
     * needs today's session" -- both are running, both show a number, and only this tells them
     * apart. Nothing counts as continued while a repayment is unresolved: the run is not safe yet.
     */
    fun continuedToday(index: Int): Boolean = !pending && todayLevel >= index + 1
}

/**
 * Applies [todayPoints] to the settled state, mirroring `cv_streak_step` with `is_today = true`.
 *
 * Today is never judged, at ANY level. Points logged so far can only carry a level forward; a
 * level they have not reached keeps standing at the run it already holds, because the day is not
 * over and one more set can still get there. Only midnight settles it -- see the migration
 * 20260909130000, which fixed the same rule server-side.
 *
 * The exception is a streak still awaiting repayment: it holds every number it has rather than
 * taking today's +1, since that increment only survives the night if today clears
 * [Streak.repayMinLevel].
 */
fun Streak.previewWith(todayPoints: Double): StreakPreview {
    val settled = levelsThroughYesterday
    val todayLevel = settled.count { todayPoints >= it.minPoints }
    val repaid = pendingThroughYesterday && todayLevel >= repayMinLevel
    val stillPending = pendingThroughYesterday && !repaid

    val levels = if (stillPending) {
        settled
    } else {
        settled.mapIndexed { index, level ->
            val streak = if (todayLevel >= index + 1) level.streak + 1 else level.streak
            level.copy(streak = streak, best = maxOf(level.best, streak))
        }
    }
    return StreakPreview(levels, todayLevel = todayLevel, pending = stillPending, repaid = repaid)
}

/** The state as the server reports it, for screens that are not previewing an edit in progress. */
fun Streak.asPreview(): StreakPreview =
    StreakPreview(levels, todayLevel = todayLevel, pending = pending, repaid = false)

/** Points a pending streak needs today to survive, or null when nothing is pending. */
fun Streak.pointsToRepay(): Double? =
    levelsThroughYesterday.getOrNull(repayMinLevel - 1)?.minPoints

/**
 * Sums each participant's daily points and ranks them, best first. Ties share a rank the way SQL's
 * rank() does, so these client-side boards agree with cv_leaderboard rather than inventing an
 * arbitrary order between people on equal points.
 */
fun leaderboardFrom(days: List<DailySummary>, participants: Map<String, Participant>): List<Standing> {
    val totals = days.groupBy { it.participantId }
        .mapNotNull { (id, entries) -> participants[id]?.let { Triple(it, entries.sumOf { d -> d.points }, entries.count { d -> d.countsAsCompleted }) } }
        .sortedByDescending { it.second }

    var rank = 0
    var previousPoints: Double? = null
    return totals.mapIndexed { index, (participant, points, completedDays) ->
        if (points != previousPoints) {
            rank = index + 1
            previousPoints = points
        }
        Standing(participant, points, rank, completedDays = completedDays)
    }
}

/** Identifies one editable cell: whose entry, for which exercise. The day is held alongside. */
data class EntryKey(val participantId: String, val exerciseTypeId: String)

/** Cells to write and cells to remove, as worked out by [EntryEdits.changes]. */
data class EntryChanges(val writes: List<EntryWrite>, val deletions: List<EntryKey>) {
    val isEmpty: Boolean get() = writes.isEmpty() && deletions.isEmpty()
}

/** Decimal keyboards in Czech locales use a comma; accept it everywhere entry text is read. */
private fun String.entryValueOrNull(): Double? = trim().replace(',', '.').toDoubleOrNull()

/**
 * The state of one day's editor: what the server had ([loaded]) next to what the user has typed
 * ([typed]). Keeping both lets [changes] send only cells that actually moved -- which saves a pile
 * of requests, and means the grid never overwrites a row nobody touched with a stale value read
 * minutes earlier.
 */
data class EntryEdits(
    private val loaded: Map<EntryKey, Double> = emptyMap(),
    private val typed: Map<EntryKey, String> = emptyMap(),
) {
    /** Raw text for an input field. */
    fun textOf(key: EntryKey): String = typed[key] ?: ""

    /** Current numeric value; blank or unparseable text counts as zero. */
    fun valueOf(key: EntryKey): Double = typed[key]?.entryValueOrNull() ?: 0.0

    fun withText(key: EntryKey, text: String): EntryEdits = copy(typed = typed + (key to text))

    /** Sets a value from the `<`/`>`/reset buttons; zero clears the field rather than showing "0". */
    fun withValue(key: EntryKey, value: Double): EntryEdits =
        withText(key, if (value == 0.0) "" else formatNumber(value))

    /** Total points across [types] for one participant. */
    fun pointsOf(participantId: String, types: List<ExerciseType>): Double =
        types.sumOf { it.pointsFor(valueOf(EntryKey(participantId, it.id))) }

    /** What still needs to be sent to the server. */
    fun changes(types: List<ExerciseType>): EntryChanges {
        val writes = mutableListOf<EntryWrite>()
        val deletions = mutableListOf<EntryKey>()
        val typesById = types.associateBy { it.id }
        for (key in loaded.keys + typed.keys) {
            val type = typesById[key.exerciseTypeId] ?: continue
            val current = typed[key]?.entryValueOrNull()?.takeIf { it > 0 }
            val previous = loaded[key]
            when {
                current == null && previous != null -> deletions += key
                current != null && current != previous -> writes += EntryWrite(key, current, type.pointsFor(current))
            }
        }
        return EntryChanges(writes, deletions)
    }

    /** Folds a successful save back in, so a second save does not resend the same rows. */
    fun committed(changes: EntryChanges): EntryEdits = copy(
        loaded = loaded - changes.deletions.toSet() + changes.writes.associate { it.key to it.rawValue },
    )

    companion object {
        /**
         * Builds the editor state from what the server returned. Values go through [formatNumber]
         * first so the baseline matches exactly what the input shows -- otherwise a value the user
         * never touched could still look "changed" on save.
         */
        fun from(entries: List<Entry>): EntryEdits {
            val typed = entries.associate {
                EntryKey(it.participantId, it.exerciseTypeId) to formatNumber(it.rawValue)
            }
            return EntryEdits(
                loaded = typed.mapNotNull { (key, text) -> text.entryValueOrNull()?.let { key to it } }.toMap(),
                typed = typed,
            )
        }
    }
}

/** Saves one day's pending changes, grouping deletions per participant into one request each. */
suspend fun EntryChanges.save(token: String, date: String, actingParticipantId: String) {
    Api.upsertEntries(token, date, writes, actingParticipantId)
    deletions.groupBy { it.participantId }.forEach { (participantId, keys) ->
        Api.deleteEntries(token, date, participantId, keys.map { it.exerciseTypeId })
    }
}
